import { React, Fragment, useEffect, useState } from "react";
import "@fortawesome/react-fontawesome";
import "@fortawesome/free-solid-svg-icons";
import { Dialog, Transition } from "@headlessui/react";
import { MoreVertical, Link as LinkIcon, X } from "lucide-react";
import "react-avatar";
import "moment";
import axios from "axios";
import { toastify } from "../../components/Toast";
import { useNavigate } from "react-router-dom";
import RichDescription from "../../components/RichDescription/RichDescription";
import { replaceEmojiImagesWithAlt } from "../../components/RichDescription/descriptionSecurity";
import { resolveCtaData } from "./includes/ctaUtils";
import CardCtaSection from "./includes/CardCtaSection";
import CardVideoPlayer from "./includes/CardVideoPlayer";

// Platform icons
import FbIcon from "../../assets/images/fbnewlogo.svg";
import twitterlogo from "../../assets/images/twitternewlogo.svg";
import defaultUser from "../../assets/images/default-user.png";
import youtubeIcon from "../../assets/images/youtube.svg";
import tiktokIcon from "../../assets/images/download-tiktok.png";
import instagramIcon from "../../assets/images/instagram-icon-png.png";
import ZimzeeIcon from "../../assets/icons/logoSymbol.svg";
import audienceIcon from "../../assets/icons/Audience.jpg";
import messengerIcon from "../../assets/icons/FacebookMessenger.png";
import FullScreen from "../../assets/icons/FullScreen.svg";
import RegionalScreen from "../../assets/icons/RegionalScreenshot.svg";
import Highlight from "../../assets/icons/HighlightText.svg";


/**
 * Extract plain text from HTML by stripping all tags
 */
function extractTextFromHtml(html) {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    replaceEmojiImagesWithAlt(doc.body);
    doc.querySelectorAll("script, style, svg, canvas").forEach(el => el.remove());
    doc.querySelectorAll('[role="button"]').forEach(el => {
      if (el.textContent?.trim() === "See more") el.remove();
    });
    let text = doc.body?.textContent || "";
    text = text.replace(/\s+/g, " ").trim();
    text = text
      .replace(/Active|Inactive|With Issues/gi, (m) => { return ""; })
      .replace(/Library\s*ID:\s*\d+/gi, "")
      .replace(/Started\s+running\s+on\s+\d{1,2}\s+\w+\s+\d{4}/gi, "")
      .replace(/Platforms?/gi, "")
      .replace(/This\s+ad\s+has\s+multiple\s+versions/gi, "")
      .replace(/Open\s+Drop-?down/gi, "")
      .replace(/See\s+ad\s+details/gi, "")
      .replace(/Sponsored/gi, "")
      .replace(/\s+/g, " ")
      .trim();
    return text;
  } catch (e) {
    return "";
  }
}


/**
 * Get the platform icon source for a given card type
 */
function getPlatformIcon(cardType) {
  switch (cardType) {
    case "twitter":
      return twitterlogo;
    case "facebook":
    case "Facebook":
      return FbIcon;
    case "youtube":
      return youtubeIcon;
    case "TikTok":
      return tiktokIcon;
    case "instagram":
      return instagramIcon;
    case "screenshoot":
      return ZimzeeIcon;
    case "fullScreen":
      return FullScreen;
    case "partScreen":
      return RegionalScreen;
    case "highlightText":
      return Highlight;
    case "messenger":
    case "facebook messenger":
    case "facebook_messenger":
      return messengerIcon;
    case "audience network":
    case "audience":
      return audienceIcon;
    default:
      return defaultUser;
  }
}

/**
 * DetailCard — Card preview component for the CardDetail page.
 * Based on NewSingleCard but without checkbox/selection UI.
 */
function DetailCard({
  title,
  content,
  note,
  tags,
  updated,
  sharedLength,
  cardId,
  starred,
  cardType,
  cardUpdate,
  onTitleChange,
  buckets,
  postDetail,
  handleSharePop,
  SharedToken,
  PermissionState,
  shared,
  sharedBy,
  PublicBucket = false,
  aiData,
  adData,
  showCopy = true,
  computedAspectRatio,
}) {
  useState(starred);
  const [showFullText, setShowFullText] = useState(false);
  const [displayTitle, setDisplayTitle] = useState(title);
  useEffect(() => {
    setDisplayTitle(title);
  }, [title, cardId]);
  let [isOpenss, setIsOpens] = useState(false);
  const [openDuplicate, setOpenDuplicate] = useState(false);
  const [openMove, setOpenMove] = useState(false);
  const [openCopy, setOpenCopy] = useState(false);
  const [optionSelect, ] = useState("");
  const [selectedBucket, setSelectedBucket] = useState("");
  const [searchBucket, setSearchBucket] = useState("");
  const [, setIsBucketOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openCopyLink, setOpenCopyLink] = useState(false);
  const [EditFile, setEditFile] = useState("");
  const [publicUrl, setPublicUrl] = useState("");
  useState("");
  const [showScreenshotPopup, setShowScreenshotPopup] = useState(false);
  useNavigate();
  const [iOS] = useState(
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
  );
  const resolveTheme = (val) => {
    if (val === "system" || !val) {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return val;
  };
  const [appTheme, setAppTheme] = useState(
    resolveTheme(localStorage.getItem("theme"))
  );

  const actualTheme = appTheme;

  useEffect(() => {
    const handleStorageChange = () => {
      setAppTheme(resolveTheme(localStorage.getItem("theme")));
    };
    window.addEventListener("storage", handleStorageChange);
    setAppTheme(resolveTheme(localStorage.getItem("theme")));

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemChange = () => {
      const stored = localStorage.getItem("theme");
      if (stored === "system" || !stored) {
        setAppTheme(mediaQuery.matches ? "dark" : "light");
      }
    };
    mediaQuery.addEventListener("change", handleSystemChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      mediaQuery.removeEventListener("change", handleSystemChange);
    };
  }, []);



  // ----- Derive display data from AI + existing fields -----
  const brandName = aiData?.brandName || displayTitle || "Unknown";
  const cardTitle = displayTitle || brandName;
  const brandLogo = postDetail?.imageUrl || (cardType === 'instagram'
    ? getPlatformIcon(cardType)
    : aiData?.brandLogo || getPlatformIcon(cardType));

  let description = aiData?.description
    || adData?.description
    || extractTextFromHtml(content?.html)
    || "";
  const descriptionHtml = aiData?.descriptionHtml || adData?.descriptionHtml || "";
  const descriptionLineHeight = aiData?.descriptionLineHeight || adData?.descriptionLineHeight;
  const hasDescription = Boolean(descriptionHtml?.trim?.() || description);
  // Strip brand name from start of description (it's already shown in card header)
  if (description && brandName && brandName !== "Unknown") {
    const escaped = brandName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    description = description.replace(new RegExp(`^\\s*${escaped}\\s*`, 'i'), '').trim();
  }

  // Replace literal '\n' string with actual newline character, and handle \n\n
  if (description) {
    description = description.replace(/\\n/g, '\n');
    // Keep paragraph breaks compact by collapsing consecutive newlines to one.
    description = description.replace(/\n(?:[ \t]*\n)+/g, '\n');
  }






  const primaryMedia = content?.imageUrl?.[0];
  const primaryMediaIsVideo =
    cardType?.toLowerCase() === "video" ||
    primaryMedia?.urlType?.toLowerCase() === "video" ||
    adData?.["Format"]?.toLowerCase() === "video" ||
    /\.(mp4|mov|webm)(?:[?#]|$)/i.test(primaryMedia?.url || "");
  let mainImage = primaryMediaIsVideo
    ? primaryMedia?.url
    : primaryMedia?.metaData?.defaultImage || primaryMedia?.url || null;
  let isVideo = Boolean(primaryMediaIsVideo && primaryMedia?.url);
  let videoMedia = primaryMediaIsVideo ? primaryMedia : null;

  if (cardType?.toLowerCase()?.includes('instagram') && Array.isArray(content?.imageUrl)) {
    const videoItem = content.imageUrl.find((img) =>
      img?.urlType?.toLowerCase() === "video" ||
      /\.(mp4|mov|webm)(?:[?#]|$)/i.test(img?.url || "")
    );
    if (videoItem?.url) {
      mainImage = videoItem.url;
      isVideo = true;
      videoMedia = videoItem;
    }
  }

  const videoPoster = videoMedia?.metaData?.thumbnail
    || videoMedia?.metaData?.defaultImage
    || (primaryMedia !== videoMedia && primaryMedia?.urlType?.toLowerCase() === "image"
      ? primaryMedia?.metaData?.defaultImage || primaryMedia?.url
      : undefined);

  const backgroundColor = content?.imageUrl?.[0]?.metaData?.backgroundColor || null;


  // ----- Handlers -----






  const handleCardLink = async () => {
    const token = localStorage.getItem("token");
    if (!PublicBucket) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API}cards/public-url`,
          { itemId: cardId, isPublic: true, itemType: "Card" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response?.data?.data) {
          if (iOS) {
            setOpenCopyLink(true);
            setPublicUrl(response?.data?.data);
          } else {
            const tempInput = document.createElement("input");
            tempInput.value = response?.data?.data;
            document.body.appendChild(tempInput);
            tempInput.select();
            document.execCommand("copy");
            document.body.removeChild(tempInput);
            toastify("success", response.data.message);
          }
        }
      } catch (error) {
        toastify("error", error?.response?.data?.message);
      }
    } else {
      const tempInput = document.createElement("input");
      tempInput.value = "https://share.zimzee.com/public/card/" + cardId;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand("copy");
      document.body.removeChild(tempInput);
      toastify("success", "Public link is copied to clipboard");
    }
  };

  function closeCardModal() {
    setIsOpens(false);
  }

  const handleDeleteCard = async (Id) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API}cards/delete`,
        { data: { deletedIds: Id }, headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response.data.message);
      cardUpdate();
      closeCardModal();
    } catch (error) {
      console.error(error);
      closeCardModal();
      toastify("error", error.response.data.message);
    }
  };

  const handleUpdateCardMenuName = async (Id) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        { cardId: Id, title: EditFile },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response.data.message);
      if (response) {
        setOpenEdit(false);
        setDisplayTitle(EditFile);
        if (onTitleChange) onTitleChange(Id, EditFile);
        else cardUpdate();
      }
    } catch (error) {
      console.error(error);
      toastify("error", error.response.data.message);
    }
  };

  const handleUpdateCardMenu = async (Id) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        { optionType: optionSelect, destinationBucketID: selectedBucket, cardId: Id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response.data.message);
      if (response) {
        cardUpdate();
        setOpenCopy(false);
      }
      setSelectedBucket("");
    } catch (error) {
      console.error(error);
      setSelectedBucket("");
      toastify("error", error.response.data.message);
    }
  };





  const handleBucketChange = (bucket) => {
    setSelectedBucket(bucket._id);
    setSearchBucket(bucket.name);
    setIsBucketOpen(false);
  };

  const handleSubBucketSelectChange = (subbucket) => {
    setSelectedBucket(subbucket._id);
    setSearchBucket(subbucket.name);
    setIsBucketOpen(false);
  };

  const handleBucketInputChange = (event) => {
    setSearchBucket(event.target.value.toLowerCase());
  };

  const filteredBuckets = buckets?.filter((bucket) => {
    const bucketNameMatches = bucket.name
      .toLowerCase()
      .includes(searchBucket);
    const subBucketMatches = bucket.subBuckets?.some((subBucket) =>
      subBucket.name.toLowerCase().includes(searchBucket)
    );
    return bucketNameMatches || subBucketMatches;
  });



  const getYoutubeEmbedUrl = (url) => {
    if (!url) return "";
    let videoId = "";
    if (url.includes("watch?v=")) {
      videoId = url.split("watch?v=")[1].split("&")[0];
    } else if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1].split("?")[0];
    } else if (url.includes("embed/")) {
      return url;
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  // ----- RENDER -----
  return (
    <div className="flex justify-center w-full">
      <div className={`rounded-3xl shadow-2xl w-full ${computedAspectRatio === "16:9" ? "max-w-[800px]" : computedAspectRatio === "9:16" ? "max-w-[400px]" : ["4:3", "3:2"].includes(computedAspectRatio) ? "max-w-[800px]" : "max-w-[600px]"} overflow-hidden transform transition-all border ${actualTheme === "dark" ? "bg-[#111111] border-white/10 shadow-black/50" : "bg-white border-gray-200"
        }`}
      >
        {/* Card Header */}
        <div className="p-3.5 space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              {brandLogo ? (
                <img
                  src={brandLogo}
                  alt="brand"
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0 ring-1 ring-white/10"
                  onError={(e) => { e.target.src = defaultUser; }}
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                  {cardTitle ? cardTitle[0].toUpperCase() : "U"}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`font-bold text-[14px] leading-tight truncate ${actualTheme === "dark" ? "text-white" : "text-gray-900"}`}>{cardTitle}</span>
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <span className={`text-[12px]  leading-none ${actualTheme === "dark" ? "text-gray-500" : "text-gray-500"}`}>
                    Sponsored
                  </span>
                  <div className={`w-3 h-3 flex items-center justify-center rounded-full bg-gray-400 text-white text-[8px]`}>
                    ✓
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0 ml-2">
              <button
                aria-label="Edit card name"
                onClick={() => { setOpenEdit(true); setEditFile(displayTitle || ""); }}
                className={`inline-flex items-center justify-center rounded-xl h-7 w-7 transition-colors ${actualTheme === "dark" ? "text-gray-400 hover:bg-zinc-800" : "text-gray-600 hover:bg-gray-100"}`}
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); closeCardModal(); }}
                className={`inline-flex items-center justify-center rounded-xl h-7 w-7 transition-colors ${actualTheme === "dark" ? "text-gray-400 hover:bg-zinc-800" : "text-gray-600 hover:bg-gray-100"}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Description below header */}
          {(!backgroundColor && hasDescription) && (
            <RichDescription
              descriptionHtml={descriptionHtml}
              description={description}
              descriptionLineHeight={descriptionLineHeight}
              maxWords={20}
              expanded={showFullText}
              onToggle={setShowFullText}
              className={`text-[14px] leading-snug ${actualTheme === "dark" ? "text-gray-300" : "text-gray-800"}`}
              toggleClassName="font-semibold text-gray-500 hover:text-gray-700 ml-1"
            />
          )}
        </div>

        {/* Card Media Section */}
        <div className="relative flex items-center justify-center w-full overflow-hidden">
          {mainImage && isVideo ? (
            <CardVideoPlayer key={mainImage} src={mainImage} poster={videoPoster} title={title} className="max-h-[1000px]" />
          ) : mainImage ? (
            <div className={`relative w-full ${(cardType === "partScreen" || cardType === "fullScreen") ? "aspect-[9/16] overflow-y-auto screenshot-scrollbar" : ""}`}>
              {backgroundColor ? (
                <div
                  className="w-full aspect-square flex items-center justify-center p-6 text-center text-white"
                  style={{ backgroundColor }}
                >
                  <RichDescription
                    descriptionHtml={descriptionHtml}
                    description={description}
                    descriptionLineHeight={descriptionLineHeight}
                    className="text-xl font-semibold break-words"
                  />
                </div>
              ) : (
                <img
                  src={mainImage}
                  alt={title}
                  className="w-full h-auto block transition-transform duration-300"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowScreenshotPopup(true);
                  }}
                  onError={(e) => { e.target.src = "/placeholder.svg"; }}
                />
              )}
              <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCardLink();
                  }}
                  className={`h-8 w-8 rounded-xl backdrop-blur-md shadow-lg flex items-center justify-center transition-colors ${actualTheme === "dark"
                    ? "bg-[#111111]/90 hover:bg-[#111111] text-white border border-white/10"
                    : "bg-white/90 hover:bg-white text-gray-900 border border-gray-200"
                    }`}
                >
                  <LinkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : cardType?.toLowerCase() === 'youtube' && content?.sourceUrl ? (
            <div className="relative w-full aspect-video overflow-hidden">
              <iframe
                className="w-full h-full"
                src={getYoutubeEmbedUrl(content.sourceUrl)}
                title={title || "YouTube video player"}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          ) : (
            <div className="w-full aspect-video bg-gray-100 dark:bg-zinc-900 flex items-center justify-center">
              <span className="text-gray-400">No media available</span>
            </div>
          )}
        </div>

        {/* Card Footer / CTA Section */}
        <CardCtaSection
          cta={resolveCtaData({ content, aiData, adData, title })}
          variant="detail"
          actualTheme={actualTheme}
        />


      </div>


      {/* ===== EXISTING MODALS (preserved logic) ===== */}
      {/* ... (Modals remain same, they were already at the end of the file) */}

      {/* Delete Confirmation Modal */}
      <Transition appear show={isOpenss} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeCardModal}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all text-center">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Delete</Dialog.Title>
                  <div className="mt-2"><p className="text-sm text-gray-500">Are you sure you want to delete?</p></div>
                  <div className="mt-4">
                    <button style={{ background: "#FF6600", width: "80px", color: "#fff", padding: "10px", borderRadius: "50px" }} type="button" className="inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium" onClick={() => handleDeleteCard(cardId)}>Yes</button>
                    <button style={{ background: "#000", width: "80px", color: "#fff", padding: "10px", borderRadius: "50px" }} type="button" className="ml-4 inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium" onClick={closeCardModal}>No</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Edit Name Modal */}
      <Transition appear show={openEdit} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setOpenEdit(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Edit Name</Dialog.Title>
                  <div className="mt-4"><input type="text" value={EditFile} onChange={(e) => setEditFile(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button style={{ background: "#000", color: "#fff", padding: "8px 20px", borderRadius: "50px" }} onClick={() => setOpenEdit(false)}>Cancel</button>
                    <button style={{ background: "#FF6600", color: "#fff", padding: "8px 20px", borderRadius: "50px" }} onClick={() => handleUpdateCardMenuName(cardId)}>Save</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Duplicate Confirmation Modal */}
      <Transition appear show={openDuplicate} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setOpenDuplicate(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all text-center">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Duplicate</Dialog.Title>
                  <div className="mt-2"><p className="text-sm text-gray-500">Are you sure you want to duplicate?</p></div>
                  <div className="mt-4">
                    <button style={{ background: "#FF6600", width: "80px", color: "#fff", padding: "10px", borderRadius: "50px" }} onClick={() => handleUpdateCardMenu(cardId)}>Yes</button>
                    <button style={{ background: "#000", width: "80px", color: "#fff", padding: "10px", borderRadius: "50px" }} className="ml-4" onClick={() => setOpenDuplicate(false)}>No</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Move To Modal */}
      <Transition appear show={openMove} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => { setOpenMove(false); setSearchBucket(""); }}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Move To</Dialog.Title>
                  <div className="custom-list-dropdown mt-4">
                    <div className="fliesbucket-inputcontent filesbottom-input">
                      <input type="text" placeholder="Select a bucket" value={searchBucket} onChange={handleBucketInputChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      <div className="tab-scrolls singlecard mt-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                        <ul className="dropdown">
                          {filteredBuckets?.map((option) => (
                            <li key={option._id} className="py-1">
                              <p className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-sm" onClick={() => handleBucketChange(option)}>{option.name}</p>
                              {option.subBuckets?.length > 0 && (
                                <ul className="pl-4">
                                  {option.subBuckets.map((sub) => (
                                    <li key={sub._id}><p className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-sm" onClick={() => handleSubBucketSelectChange(sub)}>-- {sub.name}</p></li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                      <button style={{ background: "#000", color: "#fff", padding: "8px 20px", borderRadius: "50px" }} onClick={() => { setOpenMove(false); setSearchBucket(""); }}>Cancel</button>
                      <button style={{ background: "#FF6600", color: "#fff", padding: "8px 20px", borderRadius: "50px" }} onClick={() => handleUpdateCardMenu(cardId)}>Move</button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Copy To Modal */}
      <Transition appear show={openCopy} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => { setOpenCopy(false); setSearchBucket(""); }}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Copy To</Dialog.Title>
                  <div className="custom-list-dropdown mt-4">
                    <div className="fliesbucket-inputcontent filesbottom-input">
                      <input type="text" placeholder="Select a bucket" value={searchBucket} onChange={handleBucketInputChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      <div className="tab-scrolls singlecard mt-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                        <ul className="dropdown">
                          {filteredBuckets?.map((option) => (
                            <li key={option._id} className="py-1">
                              <p className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-sm" onClick={() => handleBucketChange(option)}>{option.name}</p>
                              {option.subBuckets?.length > 0 && (
                                <ul className="pl-4">
                                  {option.subBuckets.map((sub) => (
                                    <li key={sub._id}><p className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-sm" onClick={() => handleSubBucketSelectChange(sub)}>-- {sub.name}</p></li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                      <button style={{ background: "#000", color: "#fff", padding: "8px 20px", borderRadius: "50px" }} onClick={() => { setOpenCopy(false); setSearchBucket(""); }}>Cancel</button>
                      <button style={{ background: "#FF6600", color: "#fff", padding: "8px 20px", borderRadius: "50px" }} onClick={() => handleUpdateCardMenu(cardId)}>Copy</button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Copy Link Modal (iOS) */}
      <Transition appear show={openCopyLink} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setOpenCopyLink(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Public Link</Dialog.Title>
                  <div className="mt-4"><input type="text" value={publicUrl} readOnly className="publicUrlInput w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                  <div className="mt-4 flex justify-end">
                    <button style={{ background: "#FF6600", color: "#fff", padding: "8px 20px", borderRadius: "50px" }} onClick={() => {
                      const el = document.querySelector('.publicUrlInput');
                      el.select();
                      document.execCommand('copy');
                      toastify("success", "Public link is copied to clipboard");
                      setOpenCopyLink(false);
                    }}>Copy Link</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Screenshot Full View Modal */}
      <Transition appear show={showScreenshotPopup} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setShowScreenshotPopup(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            {/* 1. Background Overlay: dark opaque bg */}
            <div className="fixed inset-0 bg-[rgba(0,0,0,0.82)] transition-opacity" />
          </Transition.Child>

          {/* 1. Background Overlay: layout & spacing */}
          <div className="fixed inset-0 z-[9999] grid place-items-center pt-[42px] px-[28px] pb-[40px]">
            {/* 2. Close Button */}
            <button
              onClick={() => setShowScreenshotPopup(false)}
              className="fixed top-[20px] right-[22px] rounded-full w-[40px] h-[40px] bg-[rgba(255,255,255,0.14)] text-white grid place-items-center hover:bg-[rgba(255,255,255,0.2)] transition-colors z-[10000]"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              {/* 3. Image Container */}
              <Dialog.Panel className="w-[min(1100px,94vw)] max-h-[calc(100vh-96px)] overflow-auto rounded-[16px] bg-[#111] shadow-2xl relative">
                <div className="flex items-center justify-center cursor-pointer w-full h-full" onClick={() => window.open(mainImage, "_blank")}>
                  {/* 4. The Image Itself */}
                  <img
                    src={mainImage}
                    alt="Full Screenshot"
                    className="w-full h-auto object-contain max-h-[calc(100vh-96px)] bg-[#111] rounded-[16px]"
                    title="Click to open in new tab"
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default DetailCard;
